/**
 * @dupe/generative-ui - Catalog Schema
 *
 * Defines the json-render UITree contract for research reports.
 * This schema is shared between the API (generation) and web (rendering).
 *
 * Key concepts:
 * - UITree: A flat tree structure with { root, elements } format
 * - Elements: Components with key, type, props, and children references
 * - Bindings: Data paths to dynamically inject values at render time
 * - Versions: Pinned versions for renderer, catalog, theme, and components
 */

import { z } from 'zod'

// =============================================================================
// VERSION CONSTANTS
// =============================================================================

export const CATALOG_VERSION = '1.3.0'
export const RENDERER_VERSION = '1.0.0'
export const THEME_VERSION = '1.0.0'
export const GENERATOR_VERSION = '1.1.0'

// =============================================================================
// DATA BINDING SCHEMAS
// =============================================================================

/**
 * Data binding path - supports both string and object formats
 * String paths: "/report/title"
 * Object paths: { path: "/report/title", transform?: "uppercase" }
 */
export const DataBindingPathSchema = z.union([
  z.string().startsWith('/'), // JSON pointer style path
  z.object({
    path: z.string().startsWith('/'),
    transform: z
      .enum(['uppercase', 'lowercase', 'capitalize', 'currency', 'percent'])
      .optional(),
    fallback: z.string().optional(),
  }),
])

export type DataBindingPath = z.infer<typeof DataBindingPathSchema>

const NumericCoerceSchema = z.preprocess((value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
      return Number(trimmed)
    }
  }
  return value
}, z.number())

const OptionalNumericCoerceSchema = z.preprocess((value) => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    const lower = trimmed.toLowerCase()
    if (lower === 'auto' || lower === 'normal') return undefined
    const normalized = trimmed.replace(/px$/i, '')
    if (normalized !== '' && !Number.isNaN(Number(normalized))) {
      return Number(normalized)
    }
    return undefined
  }
  return value
}, z.number().optional())

// =============================================================================
// ACTION SCHEMAS
// =============================================================================

/**
 * Action IDs for interactive components
 * dupe_product: Routes to chats/[...parts].tsx with product context
 */
export const ActionIdSchema = z.enum([
  'dupe_product', // Route to chat with product
  'navigate', // Generic navigation
  'open_external', // Open external URL
  'copy_to_clipboard', // Copy text
  'share', // Share via native share API
])

export type ActionId = z.infer<typeof ActionIdSchema>

export const ActionSchema = z.object({
  id: ActionIdSchema,
  payload: z.record(z.string(), z.unknown()).optional(),
})

export type Action = z.infer<typeof ActionSchema>

// =============================================================================
// COMPONENT PROP SCHEMAS
// =============================================================================

// --- Layout Components ---

export const BoxPropsSchema = z.object({
  className: z.string().optional(),
  style: z.record(z.string(), z.string()).optional(),
})

export const FlexPropsSchema = z.object({
  direction: z
    .enum(['row', 'column', 'row-reverse', 'column-reverse'])
    .optional(),
  wrap: z.enum(['wrap', 'nowrap', 'wrap-reverse']).optional(),
  justify: z
    .enum(['start', 'end', 'center', 'between', 'around', 'evenly'])
    .optional(),
  align: z.enum(['start', 'end', 'center', 'stretch', 'baseline']).optional(),
  gap: z.union([z.number(), z.string()]).optional(),
  className: z.string().optional(),
})

export const GridPropsSchema = z.object({
  columns: z.union([z.number(), z.string()]).optional(),
  rows: z.union([z.number(), z.string()]).optional(),
  gap: z.union([z.number(), z.string()]).optional(),
  className: z.string().optional(),
})

export const SectionPropsSchema = z.object({
  id: z.string().optional(),
  className: z.string().optional(),
})

// --- Typography Components ---

export const HeadingPropsSchema = z.object({
  level: z.enum(['1', '2', '3', '4', '5', '6']).optional(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TextPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  variant: z.enum(['body', 'lead', 'small', 'muted', 'code']).optional(),
  className: z.string().optional(),
})

export const LinkPropsSchema = z.object({
  href: z.string(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  external: z.boolean().optional(),
  className: z.string().optional(),
})

// --- Interactive Components ---

export const ButtonPropsSchema = z.object({
  variant: z
    .enum(['default', 'destructive', 'outline', 'secondary', 'ghost', 'link'])
    .optional(),
  size: z.enum(['default', 'xs', 'sm', 'lg', 'icon']).optional(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  disabled: z.boolean().optional(),
  processing: z.boolean().optional(),
  action: ActionSchema.optional(),
  className: z.string().optional(),
})

export const BadgePropsSchema = z.object({
  variant: z
    .enum([
      'default',
      'secondary',
      'destructive',
      'action',
      'purple',
      'blue',
      'outline',
    ])
    .optional(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const CheckboxPropsSchema = z.object({
  id: z.string().optional(),
  checked: z.boolean().optional(),
  disabled: z.boolean().optional(),
  label: z.string().optional(),
  className: z.string().optional(),
})

// --- Form Components ---

export const InputPropsSchema = z.object({
  type: z
    .enum(['text', 'email', 'password', 'number', 'tel', 'url', 'search'])
    .optional(),
  placeholder: z.string().optional(),
  value: z.union([z.string(), DataBindingPathSchema]).optional(),
  disabled: z.boolean().optional(),
  className: z.string().optional(),
})

export const LabelPropsSchema = z.object({
  htmlFor: z.string().optional(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TextareaPropsSchema = z.object({
  placeholder: z.string().optional(),
  value: z.union([z.string(), DataBindingPathSchema]).optional(),
  rows: z.number().optional(),
  disabled: z.boolean().optional(),
  className: z.string().optional(),
})

export const SelectPropsSchema = z.object({
  placeholder: z.string().optional(),
  value: z.union([z.string(), DataBindingPathSchema]).optional(),
  options: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .optional(),
  disabled: z.boolean().optional(),
  className: z.string().optional(),
})

export const ProgressPropsSchema = z.object({
  value: z
    .union([OptionalNumericCoerceSchema, DataBindingPathSchema])
    .optional(),
  max: z.union([OptionalNumericCoerceSchema, DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const SeparatorPropsSchema = z.object({
  orientation: z.enum(['horizontal', 'vertical']).optional(),
  decorative: z.boolean().optional(),
  className: z.string().optional(),
})

export const ScrollAreaPropsSchema = z.object({
  className: z.string().optional(),
})

export const ScrollBarPropsSchema = z.object({
  orientation: z.enum(['horizontal', 'vertical']).optional(),
  className: z.string().optional(),
})

// --- Data Display Components ---

export const AvatarPropsSchema = z.object({
  src: z.union([z.string(), DataBindingPathSchema]).optional(),
  alt: z.union([z.string(), DataBindingPathSchema]).optional(),
  fallback: z.string().optional(),
  className: z.string().optional(),
})

export const ImagePropsSchema = z.object({
  src: z.union([z.string(), DataBindingPathSchema]),
  alt: z.union([z.string(), DataBindingPathSchema]).optional(),
  width: z
    .union([OptionalNumericCoerceSchema, DataBindingPathSchema])
    .optional(),
  height: z
    .union([OptionalNumericCoerceSchema, DataBindingPathSchema])
    .optional(),
  className: z.string().optional(),
})

export const SkeletonPropsSchema = z.object({
  animate: z.boolean().optional(),
  className: z.string().optional(),
})

export const CardPropsSchema = z.object({
  className: z.string().optional(),
})

export const CardHeaderPropsSchema = z.object({
  className: z.string().optional(),
})

export const CardTitlePropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const CardDescriptionPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const CardContentPropsSchema = z.object({
  className: z.string().optional(),
})

export const CardFooterPropsSchema = z.object({
  className: z.string().optional(),
})

export const AlertPropsSchema = z.object({
  variant: z.enum(['default', 'destructive']).optional(),
  className: z.string().optional(),
})

export const AlertTitlePropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const AlertDescriptionPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TablePropsSchema = z.object({
  className: z.string().optional(),
})

export const TableHeaderPropsSchema = z.object({
  className: z.string().optional(),
})

export const TableBodyPropsSchema = z.object({
  className: z.string().optional(),
})

export const TableFooterPropsSchema = z.object({
  className: z.string().optional(),
})

export const TableRowPropsSchema = z.object({
  className: z.string().optional(),
})

export const TableHeadPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TableCellPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TableCaptionPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

// --- Container Components ---

export const AccordionPropsSchema = z.object({
  type: z.enum(['single', 'multiple']).optional(),
  defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
  collapsible: z.boolean().optional(),
  className: z.string().optional(),
})

export const AccordionItemPropsSchema = z.object({
  value: z.string(),
  className: z.string().optional(),
})

export const AccordionTriggerPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const AccordionContentPropsSchema = z.object({
  className: z.string().optional(),
})

export const TabsPropsSchema = z.object({
  defaultValue: z.string().optional(),
  className: z.string().optional(),
})

export const TabsListPropsSchema = z.object({
  className: z.string().optional(),
})

export const TabsTriggerPropsSchema = z.object({
  value: z.string(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TabsContentPropsSchema = z.object({
  value: z.string(),
  className: z.string().optional(),
})

export const CarouselPropsSchema = z.object({
  orientation: z.enum(['horizontal', 'vertical']).optional(),
  className: z.string().optional(),
})

export const CarouselItemPropsSchema = z.object({
  className: z.string().optional(),
})

// --- Overlay Components ---

export const DialogPropsSchema = z.object({
  open: z.boolean().optional(),
  className: z.string().optional(),
})

export const DialogTriggerPropsSchema = z.object({
  asChild: z.boolean().optional(),
})

export const DialogContentPropsSchema = z.object({
  className: z.string().optional(),
})

export const DialogHeaderPropsSchema = z.object({
  className: z.string().optional(),
})

export const DialogTitlePropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const DialogDescriptionPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const SheetPropsSchema = z.object({
  open: z.boolean().optional(),
  className: z.string().optional(),
})

export const SheetContentPropsSchema = z.object({
  side: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  className: z.string().optional(),
})

export const DrawerPropsSchema = z.object({
  open: z.boolean().optional(),
  className: z.string().optional(),
})

export const PopoverPropsSchema = z.object({
  open: z.boolean().optional(),
})

export const PopoverContentPropsSchema = z.object({
  align: z.enum(['start', 'center', 'end']).optional(),
  sideOffset: z.number().optional(),
  className: z.string().optional(),
})

export const TooltipPropsSchema = z.object({
  content: z.union([z.string(), DataBindingPathSchema]).optional(),
  side: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  delayDuration: z.number().optional(),
})

// --- Product-Specific Components ---

export const ProductCardPropsSchema = z.object({
  slug: z.union([z.string(), DataBindingPathSchema]),
  name: z.union([z.string(), DataBindingPathSchema]).optional(),
  brand: z.union([z.string(), DataBindingPathSchema]).optional(),
  image: z.union([z.string(), DataBindingPathSchema]).optional(),
  badge: z.union([z.string(), DataBindingPathSchema]).optional(),
  summary: z.union([z.string(), DataBindingPathSchema]).optional(),
  pill: z.union([z.string(), DataBindingPathSchema]).optional(),
  ctaLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  detailsLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  price: z
    .union([
      z.string(),
      z.number(),
      DataBindingPathSchema,
      z.object({
        amount: z.union([z.number(), z.string(), DataBindingPathSchema]),
        currency: z.string().optional(),
        locale: z.string().optional(),
      }),
    ])
    .optional(),
  rating: z.union([NumericCoerceSchema, DataBindingPathSchema]).optional(),
  rank: z.union([NumericCoerceSchema, DataBindingPathSchema]).optional(),
  action: ActionSchema.optional(),
  className: z.string().optional(),
})

export const ProductGridPropsSchema = z.object({
  columns: z.number().optional(),
  gap: z.union([z.number(), z.string()]).optional(),
  className: z.string().optional(),
})

export const PriceDisplayPropsSchema = z.object({
  amount: z.union([z.number(), z.string(), DataBindingPathSchema]),
  currency: z.string().optional(),
  locale: z.string().optional(),
  className: z.string().optional(),
})

export const RatingDisplayPropsSchema = z.object({
  value: z.union([NumericCoerceSchema, DataBindingPathSchema]),
  max: z.number().optional(),
  showValue: z.boolean().optional(),
  className: z.string().optional(),
})

export const SpecsTablePropsSchema = z.object({
  specs: z
    .union([
      z.array(
        z.object({
          label: z.string(),
          value: z.union([z.string(), z.number()]),
        })
      ),
      z.record(z.string(), z.union([z.string(), z.number()])),
      DataBindingPathSchema,
    ])
    .optional(),
  className: z.string().optional(),
})

export const ProsConsPropsSchema = z.object({
  pros: z.union([z.array(z.string()), DataBindingPathSchema]).optional(),
  cons: z.union([z.array(z.string()), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

// --- Report-Specific Components ---

export const ReportHeaderPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]),
  subtitle: z.union([z.string(), DataBindingPathSchema]).optional(),
  date: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const TableOfContentsPropsSchema = z.object({
  items: z
    .union([
      z.array(
        z.object({
          id: z.string(),
          title: z.string(),
          level: z.number().optional(),
        })
      ),
      DataBindingPathSchema,
    ])
    .optional(),
  className: z.string().optional(),
})

export const ArticleSectionPropsSchema = z.object({
  id: z.string().optional(),
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  content: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const OurRecommendationTopPickSchema = z.object({
  slug: z.string(),
  name: z.union([z.string(), DataBindingPathSchema]).optional(),
  brand: z.union([z.string(), DataBindingPathSchema]).optional(),
  image: z.union([z.string(), DataBindingPathSchema]).optional(),
  badgeLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  score: z.union([z.string(), z.number(), DataBindingPathSchema]).optional(),
  reviews: z.union([z.string(), DataBindingPathSchema]).optional(),
  description: z.union([z.string(), DataBindingPathSchema]).optional(),
  tags: z.array(z.string()).optional(),
  oldPrice: z.union([z.string(), DataBindingPathSchema]).nullable().optional(),
  newPrice: z.union([z.string(), DataBindingPathSchema]).optional(),
  savings: z.union([z.string(), DataBindingPathSchema]).nullable().optional(),
  ctaLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  detailsLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  action: ActionSchema.optional(),
})

export const OurRecommendationSmartPickSchema = z.object({
  slug: z.string(),
  name: z.union([z.string(), DataBindingPathSchema]).optional(),
  image: z.union([z.string(), DataBindingPathSchema]).optional(),
  price: z.union([z.string(), DataBindingPathSchema]).optional(),
  description: z.union([z.string(), DataBindingPathSchema]).optional(),
  detailsLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  action: ActionSchema.optional(),
})

export const OurRecommendationPropsSchema = z.object({
  label: z.union([z.string(), DataBindingPathSchema]).optional(),
  heading: z.union([z.string(), DataBindingPathSchema]).optional(),
  headingEmphasis: z.union([z.string(), DataBindingPathSchema]).optional(),
  subtitle: z.union([z.string(), DataBindingPathSchema]).optional(),
  topPick: OurRecommendationTopPickSchema.optional(),
  smartPick: OurRecommendationSmartPickSchema.optional(),
  smartPickLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  refinementText: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const VerdictPropsSchema = z.object({
  label: z.union([z.string(), DataBindingPathSchema]).optional(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const ReportTopNavPropsSchema = z.object({
  brandWord: z.union([z.string(), DataBindingPathSchema]).optional(),
  brandGlyph: z.union([z.string(), DataBindingPathSchema]).optional(),
  avatarSrc: z.union([z.string(), DataBindingPathSchema]).optional(),
  avatarFallback: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const ReportActionBarPropsSchema = z.object({
  backLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  saveLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  shareLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const ReportHeroPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]),
  badges: z.union([z.array(z.string()), DataBindingPathSchema]).optional(),
  stats: z.union([z.array(z.string()), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const QuickDecisionHelperPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  subtitle: z.union([z.string(), DataBindingPathSchema]).optional(),
  options: z
    .union([
      z.array(
        z.object({
          title: z.string(),
          description: z.string(),
          icon: z
            .string()
            .trim()
            .min(1)
            .max(40)
            .regex(/^[a-zA-Z0-9_\-\s]+$/)
            .optional(),
        })
      ),
      DataBindingPathSchema,
    ])
    .optional(),
  decisionPrefix: z.union([z.string(), DataBindingPathSchema]).optional(),
  highlightText: z.union([z.string(), DataBindingPathSchema]).optional(),
  decisionSuffix: z.union([z.string(), DataBindingPathSchema]).optional(),
  ctaText: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const ReportFooterPropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  linkLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  linkHref: z.union([z.string(), DataBindingPathSchema]).optional(),
  publishedAt: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

const RankedOptionItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: z.union([z.string(), DataBindingPathSchema]).optional(),
  badge: z.union([z.string(), DataBindingPathSchema]).optional(),
  badgeTone: z.enum(['purple', 'blue', 'neutral']).optional(),
  score: z
    .union([NumericCoerceSchema, z.string(), DataBindingPathSchema])
    .optional(),
  rankLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  tags: z.union([z.array(z.string()), DataBindingPathSchema]).optional(),
  oldPrice: z.union([z.string(), DataBindingPathSchema]).optional(),
  price: z.union([z.string(), DataBindingPathSchema]).optional(),
  savings: z.union([z.string(), DataBindingPathSchema]).optional(),
  detailsLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
})

export const ReportRankedOptionsSectionPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  subtitle: z.union([z.string(), DataBindingPathSchema]).optional(),
  items: z
    .union([z.array(RankedOptionItemSchema), DataBindingPathSchema])
    .optional(),
  highlightedId: z.union([z.string(), DataBindingPathSchema]).optional(),
  isBusy: z.boolean().optional(),
  className: z.string().optional(),
})

const ResearchMetricItemSchema = z.object({
  id: z.string(),
  value: z.union([z.string(), NumericCoerceSchema]),
  label: z.string(),
  ctaLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  icon: z
    .enum(['reddit', 'youtube', 'tiktok', 'instagram', 'analytics'])
    .optional(),
})

export const ReportResearchMetricsSectionPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  description: z.union([z.string(), DataBindingPathSchema]).optional(),
  items: z
    .union([z.array(ResearchMetricItemSchema), DataBindingPathSchema])
    .optional(),
  notePrefix: z.union([z.string(), DataBindingPathSchema]).optional(),
  note: z.union([z.string(), DataBindingPathSchema]).optional(),
  isBusy: z.boolean().optional(),
  className: z.string().optional(),
})

const ComparisonScoreCellSchema = z.object({
  kind: z.literal('score'),
  value: NumericCoerceSchema,
  max: NumericCoerceSchema.optional(),
})

const ComparisonCellValueSchema = z.union([
  z.string(),
  NumericCoerceSchema,
  z.boolean(),
  ComparisonScoreCellSchema,
  z.null(),
])

const ComparisonColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.union([z.string(), DataBindingPathSchema]).optional(),
  highlighted: z.boolean().optional(),
})

const ComparisonRowSchema = z.object({
  id: z.string(),
  label: z.string(),
  values: z.array(ComparisonCellValueSchema),
})

export const ReportHeadToHeadComparisonSectionPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  subtitle: z.union([z.string(), DataBindingPathSchema]).optional(),
  columns: z.union([z.array(ComparisonColumnSchema), DataBindingPathSchema]),
  rows: z.union([z.array(ComparisonRowSchema), DataBindingPathSchema]),
  takeawayTitle: z.union([z.string(), DataBindingPathSchema]).optional(),
  takeawayText: z.union([z.string(), DataBindingPathSchema]).optional(),
  isBusy: z.boolean().optional(),
  className: z.string().optional(),
})

const ReportAreaChartDatumSchema = z.object({
  label: z.union([z.string(), DataBindingPathSchema]).optional(),
  date: z.union([z.string(), DataBindingPathSchema]).optional(),
  desktop: z
    .union([NumericCoerceSchema, z.string(), DataBindingPathSchema])
    .optional(),
  mobile: z
    .union([NumericCoerceSchema, z.string(), DataBindingPathSchema])
    .optional(),
  other: z
    .union([NumericCoerceSchema, z.string(), DataBindingPathSchema])
    .optional(),
})

export const ReportAreaChartPropsSchema = z.object({
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  description: z.union([z.string(), DataBindingPathSchema]).optional(),
  footerText: z.union([z.string(), DataBindingPathSchema]).optional(),
  footerTrend: z.union([z.string(), DataBindingPathSchema]).optional(),
  rangeLabel: z.union([z.string(), DataBindingPathSchema]).optional(),
  data: z
    .union([z.array(ReportAreaChartDatumSchema), DataBindingPathSchema])
    .optional(),
  className: z.string().optional(),
})

export const CalloutPropsSchema = z.object({
  type: z.enum(['info', 'warning', 'success', 'error', 'tip']).optional(),
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  text: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const VideoEmbedPropsSchema = z.object({
  url: z.union([z.string(), DataBindingPathSchema]),
  title: z.union([z.string(), DataBindingPathSchema]).optional(),
  startAt: z.union([NumericCoerceSchema, DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const QuotePropsSchema = z.object({
  text: z.union([z.string(), DataBindingPathSchema]),
  author: z.union([z.string(), DataBindingPathSchema]).optional(),
  source: z.union([z.string(), DataBindingPathSchema]).optional(),
  url: z.union([z.string(), DataBindingPathSchema]).optional(),
  className: z.string().optional(),
})

export const ComparisonTablePropsSchema = z.object({
  products: z
    .union([
      z.array(z.string()), // product slugs
      DataBindingPathSchema,
    ])
    .optional(),
  attributes: z.array(z.string()).optional(),
  className: z.string().optional(),
})

// =============================================================================
// COMPONENT TYPE REGISTRY
// =============================================================================

export const ComponentTypeSchema = z.enum([
  // Layout
  'Box',
  'Flex',
  'Grid',
  'Section',
  // Typography
  'Heading',
  'Text',
  'Link',
  // Interactive
  'Button',
  'Badge',
  'Checkbox',
  // Form
  'Input',
  'Label',
  'Textarea',
  'Select',
  'Progress',
  'Separator',
  'ScrollArea',
  'ScrollBar',
  // Data Display
  'Avatar',
  'Image',
  'Skeleton',
  'Card',
  'CardHeader',
  'CardTitle',
  'CardDescription',
  'CardContent',
  'CardFooter',
  'Alert',
  'AlertTitle',
  'AlertDescription',
  'Table',
  'TableHeader',
  'TableBody',
  'TableFooter',
  'TableRow',
  'TableHead',
  'TableCell',
  'TableCaption',
  // Containers
  'Accordion',
  'AccordionItem',
  'AccordionTrigger',
  'AccordionContent',
  'Tabs',
  'TabsList',
  'TabsTrigger',
  'TabsContent',
  'Carousel',
  'CarouselItem',
  // Overlays
  'Dialog',
  'DialogTrigger',
  'DialogContent',
  'DialogHeader',
  'DialogTitle',
  'DialogDescription',
  'Sheet',
  'SheetContent',
  'Drawer',
  'Popover',
  'PopoverContent',
  'Tooltip',
  // Product-Specific
  'ProductCard',
  'ProductGrid',
  'PriceDisplay',
  'RatingDisplay',
  'SpecsTable',
  'ProsCons',
  // Report-Specific
  'ReportHeader',
  'ReportTopNav',
  'ReportActionBar',
  'ReportHero',
  'ReportRankedOptionsSection',
  'ReportHeadToHeadComparisonSection',
  'ReportResearchMetricsSection',
  'ReportAreaChartDefault',
  'ReportAreaChartLinear',
  'ReportAreaChartStep',
  'ReportAreaChartStacked',
  'ReportAreaChartStackedExpand',
  'ReportAreaChartLegend',
  'ReportAreaChartGradient',
  'ReportAreaChartIcons',
  'ReportAreaChartAxes',
  'ReportAreaChartInteractive',
  'TableOfContents',
  'ArticleSection',
  'Verdict',
  'OurRecommendation',
  'QuickDecisionHelper',
  'ReportFooter',
  'Callout',
  'VideoEmbed',
  'Quote',
  'ComparisonTable',
])

export type ComponentType = z.infer<typeof ComponentTypeSchema>

// Mapping of component types to their props schemas
export const ComponentPropsSchemaMap = {
  Box: BoxPropsSchema,
  Flex: FlexPropsSchema,
  Grid: GridPropsSchema,
  Section: SectionPropsSchema,
  Heading: HeadingPropsSchema,
  Text: TextPropsSchema,
  Link: LinkPropsSchema,
  Button: ButtonPropsSchema,
  Badge: BadgePropsSchema,
  Checkbox: CheckboxPropsSchema,
  Input: InputPropsSchema,
  Label: LabelPropsSchema,
  Textarea: TextareaPropsSchema,
  Select: SelectPropsSchema,
  Progress: ProgressPropsSchema,
  Separator: SeparatorPropsSchema,
  ScrollArea: ScrollAreaPropsSchema,
  ScrollBar: ScrollBarPropsSchema,
  Avatar: AvatarPropsSchema,
  Image: ImagePropsSchema,
  Skeleton: SkeletonPropsSchema,
  Card: CardPropsSchema,
  CardHeader: CardHeaderPropsSchema,
  CardTitle: CardTitlePropsSchema,
  CardDescription: CardDescriptionPropsSchema,
  CardContent: CardContentPropsSchema,
  CardFooter: CardFooterPropsSchema,
  Alert: AlertPropsSchema,
  AlertTitle: AlertTitlePropsSchema,
  AlertDescription: AlertDescriptionPropsSchema,
  Table: TablePropsSchema,
  TableHeader: TableHeaderPropsSchema,
  TableBody: TableBodyPropsSchema,
  TableFooter: TableFooterPropsSchema,
  TableRow: TableRowPropsSchema,
  TableHead: TableHeadPropsSchema,
  TableCell: TableCellPropsSchema,
  TableCaption: TableCaptionPropsSchema,
  Accordion: AccordionPropsSchema,
  AccordionItem: AccordionItemPropsSchema,
  AccordionTrigger: AccordionTriggerPropsSchema,
  AccordionContent: AccordionContentPropsSchema,
  Tabs: TabsPropsSchema,
  TabsList: TabsListPropsSchema,
  TabsTrigger: TabsTriggerPropsSchema,
  TabsContent: TabsContentPropsSchema,
  Carousel: CarouselPropsSchema,
  CarouselItem: CarouselItemPropsSchema,
  Dialog: DialogPropsSchema,
  DialogTrigger: DialogTriggerPropsSchema,
  DialogContent: DialogContentPropsSchema,
  DialogHeader: DialogHeaderPropsSchema,
  DialogTitle: DialogTitlePropsSchema,
  DialogDescription: DialogDescriptionPropsSchema,
  Sheet: SheetPropsSchema,
  SheetContent: SheetContentPropsSchema,
  Drawer: DrawerPropsSchema,
  Popover: PopoverPropsSchema,
  PopoverContent: PopoverContentPropsSchema,
  Tooltip: TooltipPropsSchema,
  ProductCard: ProductCardPropsSchema,
  ProductGrid: ProductGridPropsSchema,
  PriceDisplay: PriceDisplayPropsSchema,
  RatingDisplay: RatingDisplayPropsSchema,
  SpecsTable: SpecsTablePropsSchema,
  ProsCons: ProsConsPropsSchema,
  ReportHeader: ReportHeaderPropsSchema,
  ReportTopNav: ReportTopNavPropsSchema,
  ReportActionBar: ReportActionBarPropsSchema,
  ReportHero: ReportHeroPropsSchema,
  ReportRankedOptionsSection: ReportRankedOptionsSectionPropsSchema,
  ReportHeadToHeadComparisonSection:
    ReportHeadToHeadComparisonSectionPropsSchema,
  ReportResearchMetricsSection: ReportResearchMetricsSectionPropsSchema,
  ReportAreaChartDefault: ReportAreaChartPropsSchema,
  ReportAreaChartLinear: ReportAreaChartPropsSchema,
  ReportAreaChartStep: ReportAreaChartPropsSchema,
  ReportAreaChartStacked: ReportAreaChartPropsSchema,
  ReportAreaChartStackedExpand: ReportAreaChartPropsSchema,
  ReportAreaChartLegend: ReportAreaChartPropsSchema,
  ReportAreaChartGradient: ReportAreaChartPropsSchema,
  ReportAreaChartIcons: ReportAreaChartPropsSchema,
  ReportAreaChartAxes: ReportAreaChartPropsSchema,
  ReportAreaChartInteractive: ReportAreaChartPropsSchema,
  TableOfContents: TableOfContentsPropsSchema,
  ArticleSection: ArticleSectionPropsSchema,
  Verdict: VerdictPropsSchema,
  OurRecommendation: OurRecommendationPropsSchema,
  QuickDecisionHelper: QuickDecisionHelperPropsSchema,
  ReportFooter: ReportFooterPropsSchema,
  Callout: CalloutPropsSchema,
  VideoEmbed: VideoEmbedPropsSchema,
  Quote: QuotePropsSchema,
  ComparisonTable: ComparisonTablePropsSchema,
} as const

// =============================================================================
// UI ELEMENT SCHEMA
// =============================================================================

/**
 * A single UI element in the tree
 */
export const UIElementSchema = z.object({
  /** Unique key for this element within the tree */
  key: z.string(),
  /** Component type from the catalog */
  type: ComponentTypeSchema,
  /** Component props (validated per component type) */
  props: z.record(z.string(), z.unknown()),
  /** Keys of child elements (references into elements map) */
  children: z.array(z.string()).optional(),
})

export type UIElement = z.infer<typeof UIElementSchema>

// =============================================================================
// UI TREE SCHEMA
// =============================================================================

/**
 * The complete UITree structure for json-render
 */
export const UITreeSchema = z.object({
  /** Key of the root element */
  root: z.string(),
  /** Map of element keys to element definitions */
  elements: z.record(z.string(), UIElementSchema),
})

export type UITree = z.infer<typeof UITreeSchema>

// =============================================================================
// PINNED PAYLOAD SCHEMA
// =============================================================================

/**
 * Complete payload stored in research_report_trees
 * Includes the tree and all version pins for deterministic rendering
 */
export const PinnedUIPayloadSchema = z.object({
  /** Renderer version (e.g., "ui-renderer@1.6.2") */
  renderer: z.string(),
  /** Catalog version (e.g., "report-catalog@1.2.0") */
  catalog: z.string(),
  /** Theme version (e.g., "brand-theme@5.0.0") */
  theme: z.string(),
  /** Per-component API versions for granular compatibility */
  componentApiVersions: z.record(z.string(), z.string()).optional(),
  /** The actual UI tree */
  tree: UITreeSchema,
})

export type PinnedUIPayload = z.infer<typeof PinnedUIPayloadSchema>

// =============================================================================
// CATALOG SCHEMA (for research_uicatalog table)
// =============================================================================

/**
 * Schema stored in research_uicatalog for version tracking
 */
export const CatalogSchemaSchema = z.object({
  version: z.string(),
  components: z.array(
    z.object({
      type: ComponentTypeSchema,
      version: z.string(),
      propsSchema: z.record(z.string(), z.unknown()), // JSON Schema representation
    })
  ),
  actions: z.array(
    z.object({
      id: ActionIdSchema,
      description: z.string(),
      payloadSchema: z.record(z.string(), z.unknown()).optional(),
    })
  ),
})

export type CatalogSchema = z.infer<typeof CatalogSchemaSchema>

// =============================================================================
// GENERATION INPUT SCHEMA
// =============================================================================

/**
 * Input for the generative UI agent
 */
export const GeneratorInputSchema = z.object({
  /** Task ID for tracking */
  taskId: z.string(),
  /** Brief markdown content */
  briefMd: z.string().optional(),
  /** Article markdown content (optional for early generation) */
  articleMd: z.string().optional(),
  /** Optional report brief for additional context */
  reportBrief: z
    .union([z.string(), z.record(z.string(), z.unknown())])
    .optional(),
  /** Optional report artifacts payload (brief/article hints) */
  reportArtifacts: z.record(z.string(), z.unknown()).optional(),
  /** Optional raw shortlist payload from candidates.json */
  candidatesJson: z.unknown().optional(),
  /** Optional raw full-pool payload from candidates-pool.json */
  candidatesPoolJson: z.unknown().optional(),
  /** Optional video insights markdown (by product slug) */
  videoInsightsData: z.record(z.string(), z.string()).optional(),
  /** Optional social/community insights markdown (by product slug) */
  socialInsightsData: z.record(z.string(), z.string()).optional(),
  /** Structured product data */
  products: z
    .array(
      z.object({
        slug: z.string(),
        name: z.string(),
        brand: z.string().optional(),
        category: z.string().optional(),
        subcategory: z.string().optional(),
        msrp: z.number().optional(),
        observedLow: z.number().optional(),
        observedHigh: z.number().optional(),
        currency: z.string().optional(),
        priceSources: z.array(z.record(z.string(), z.unknown())).optional(),
        sources: z.array(z.record(z.string(), z.unknown())).optional(),
        links: z.array(z.record(z.string(), z.unknown())).optional(),
        images: z.array(z.string()).optional(),
        attributes: z.record(z.string(), z.unknown()).optional(),
        specs: z.record(z.string(), z.unknown()).optional(),
        assessment: z.record(z.string(), z.unknown()).optional(),
        verification: z.record(z.string(), z.unknown()).optional(),
        rank: z.record(z.string(), z.unknown()).optional(),
      })
    )
    .optional(),
  /** Catalog schema to use for generation */
  catalogVersion: z.string().optional(),

  /**
   * Optional outline overrides.
   *
   * The host owns the canonical section catalog + templates, but generation can
   * be configured per-run (e.g. choose a different outline template) via these
   * overrides.
   */
  outlineMode: z.string().optional(),
  outlineTemplate: z.string().optional(),
})

export type GeneratorInput = z.infer<typeof GeneratorInputSchema>

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate a UI tree against the catalog schema
 */
export function validateUITree(tree: unknown): {
  valid: boolean
  errors: string[]
} {
  const result = UITreeSchema.safeParse(tree)
  if (!result.success) {
    // Zod v4 uses .issues instead of .errors
    const issues =
      'issues' in result.error
        ? result.error.issues
        : (result.error as any).errors || []
    return {
      valid: false,
      errors: issues.map(
        (e: { path: (string | number)[]; message: string }) =>
          `${e.path.join('.')}: ${e.message}`
      ),
    }
  }

  const errors: string[] = []
  const { root, elements } = result.data

  // Validate root exists
  if (!elements[root]) {
    errors.push(`Root element "${root}" not found in elements`)
  }

  // Validate all children references
  for (const [key, element] of Object.entries(elements)) {
    if (element.children) {
      for (const childKey of element.children) {
        if (!elements[childKey]) {
          errors.push(
            `Element "${key}" references non-existent child "${childKey}"`
          )
        }
      }
    }

    // Validate props against component schema
    const propsSchema =
      ComponentPropsSchemaMap[
        element.type as keyof typeof ComponentPropsSchemaMap
      ]
    if (propsSchema) {
      const propsResult = propsSchema.safeParse(element.props)
      if (!propsResult.success) {
        // Zod v4 uses .issues instead of .errors
        const issues =
          'issues' in propsResult.error
            ? propsResult.error.issues
            : (propsResult.error as any).errors || []
        for (const err of issues) {
          errors.push(
            `Element "${key}" (${element.type}): ${(err.path || []).join('.')}: ${err.message}`
          )
        }
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Check for circular references in the tree
 */
export function checkCircularReferences(tree: UITree): string[] {
  const errors: string[] = []
  const visited = new Set<string>()
  const recursionStack = new Set<string>()

  function dfs(key: string, path: string[]): boolean {
    if (recursionStack.has(key)) {
      errors.push(`Circular reference detected: ${[...path, key].join(' -> ')}`)
      return true
    }
    if (visited.has(key)) return false

    visited.add(key)
    recursionStack.add(key)

    const element = tree.elements[key]
    if (element?.children) {
      for (const childKey of element.children) {
        if (dfs(childKey, [...path, key])) return true
      }
    }

    recursionStack.delete(key)
    return false
  }

  dfs(tree.root, [])
  return errors
}

/**
 * Create a minimal valid tree for fallback
 */
export function createMinimalTree(title = 'Report'): UITree {
  return {
    root: 'root',
    elements: {
      root: {
        key: 'root',
        type: 'Section',
        props: { className: 'min-h-screen p-8' },
        children: ['header'],
      },
      header: {
        key: 'header',
        type: 'ReportHeader',
        props: { title },
      },
    },
  }
}

/**
 * Normalize string paths to object paths
 * Handles transitional support for string-only paths
 */
export function normalizeBindingPath(path: string | { path: string }): {
  path: string
} {
  if (typeof path === 'string') {
    return { path }
  }
  return path
}

// =============================================================================
// EXAMPLE TREES FOR TESTING
// =============================================================================

export const exampleProductReportTree: UITree = {
  root: 'root',
  elements: {
    root: {
      key: 'root',
      type: 'Section',
      props: { className: 'min-h-screen bg-white' },
      children: ['header', 'toc', 'intro', 'products', 'comparison'],
    },
    header: {
      key: 'header',
      type: 'ReportHeader',
      props: {
        title: { path: '/report/title' },
        subtitle: { path: '/report/subtitle' },
        date: { path: '/report/publishedAt' },
      },
    },
    toc: {
      key: 'toc',
      type: 'TableOfContents',
      props: {
        items: { path: '/report/sections' },
        className: 'my-8',
      },
    },
    intro: {
      key: 'intro',
      type: 'ArticleSection',
      props: {
        id: 'introduction',
        title: 'Introduction',
        content: { path: '/report/introduction' },
      },
    },
    products: {
      key: 'products',
      type: 'ProductGrid',
      props: {
        columns: 3,
        gap: '1.5rem',
        className: 'my-8',
      },
      children: ['product-1', 'product-2', 'product-3'],
    },
    'product-1': {
      key: 'product-1',
      type: 'ProductCard',
      props: {
        slug: { path: '/products/0/slug' },
        name: { path: '/products/0/name' },
        brand: { path: '/products/0/brand' },
        image: { path: '/products/0/images/0' },
        price: { path: '/products/0/msrp' },
        rank: 1,
        action: {
          id: 'dupe_product',
          payload: { slug: { path: '/products/0/slug' } },
        },
      },
    },
    'product-2': {
      key: 'product-2',
      type: 'ProductCard',
      props: {
        slug: { path: '/products/1/slug' },
        name: { path: '/products/1/name' },
        brand: { path: '/products/1/brand' },
        image: { path: '/products/1/images/0' },
        price: { path: '/products/1/msrp' },
        rank: 2,
        action: {
          id: 'dupe_product',
          payload: { slug: { path: '/products/1/slug' } },
        },
      },
    },
    'product-3': {
      key: 'product-3',
      type: 'ProductCard',
      props: {
        slug: { path: '/products/2/slug' },
        name: { path: '/products/2/name' },
        brand: { path: '/products/2/brand' },
        image: { path: '/products/2/images/0' },
        price: { path: '/products/2/msrp' },
        rank: 3,
        action: {
          id: 'dupe_product',
          payload: { slug: { path: '/products/2/slug' } },
        },
      },
    },
    comparison: {
      key: 'comparison',
      type: 'ComparisonTable',
      props: {
        products: { path: '/products' },
        attributes: ['price', 'rating', 'brand'],
        className: 'my-8',
      },
    },
  },
}
